import React from 'react';
import { motion } from 'framer-motion';
import { AnimatedSection } from '../shared/AnimatedSection';
const GOLD = '#C9A84C';
const GOLD_LIGHT = '#E8D48B';
/**
 * Big-idea / core belief shift section — sits between EmotionalPain and SignGrid.
 * Introduces "No eres caótico. Tienes un Reino interno dominante."
 * Ends with a cinematic divider that visually leads into the 12 Reinos selector.
 */
export function BigIdea() {
  return <section className="relative py-24 md:py-32 px-6 overflow-hidden bg-cosmic-black pt-[20px] pb-[20px]" data-id="element-22">
      {/* Backdrop */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-23">
        <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse at 50% 50%, rgba(201,168,76,0.09) 0%, transparent 65%)'
      }} data-id="element-24" />
      </div>

      {/* Sacred geometry — twelve-fold mandala */}
      <div className="absolute inset-0 pointer-events-none flex items-center justify-center opacity-[0.08]" data-id="element-25">
        <motion.svg width="720" height="720" viewBox="0 0 720 720" fill="none" animate={{
        rotate: -360
      }} transition={{
        duration: 300,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-26">
          <circle cx="360" cy="360" r="320" stroke={GOLD} strokeWidth="0.6" data-id="element-27" />
          <circle cx="360" cy="360" r="240" stroke={GOLD} strokeWidth="0.5" data-id="element-28" />
          <circle cx="360" cy="360" r="160" stroke={GOLD} strokeWidth="0.5" data-id="element-29" />
          {/* 12 spokes — one for each realm */}
          {Array.from({
          length: 12
        }).map((_, i) => <line key={i} x1="360" y1="40" x2="360" y2="680" stroke={GOLD} strokeWidth="0.4" transform={`rotate(${i * 30} 360 360)`} data-id="element-30" />)}
          {/* Inner star */}
          <path d="M360 200 L420 360 L360 520 L300 360 Z" stroke={GOLD} strokeWidth="0.7" data-id="element-31" />
        </motion.svg>
      </div>

      {/* Floating embers */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-32">
        {[...Array(14)].map((_, i) => <motion.div key={i} className="absolute w-[2px] h-[2px] rounded-full" style={{
        backgroundColor: GOLD,
        left: `${i * 7.7 % 100}%`,
        top: `${i * 11 % 100}%`,
        boxShadow: `0 0 8px ${GOLD}`
      }} animate={{
        y: [-15, -50, -15],
        opacity: [0.15, 0.6, 0.15]
      }} transition={{
        duration: 8 + i % 5,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: i * 0.4
      }} data-id="element-33" />)}
      </div>

      <div className="relative z-10 container mx-auto max-w-3xl text-center" data-id="element-34">
        <AnimatedSection data-id="element-35">
          <motion.div className="inline-flex items-center gap-3 mb-8" initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} data-id="element-36">
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-gold-primary" data-id="element-37" />
            <span className="tracking-[0.4em] uppercase font-serif text-gold-light text-[14px]" data-id="element-38">
              El Cambio de Perspectiva
            </span>
            <div className="h-px w-12 bg-gradient-to-r from-gold-primary to-transparent" data-id="element-39" />
          </motion.div>

          <h2 className="text-3xl md:text-5xl font-serif font-bold text-white mb-10 leading-tight px-2" style={{
          textShadow: `0 0 30px rgba(201,168,76,0.3)`
        }} data-id="element-40">
            No eres un signo.{' '}
            <span style={{
            color: GOLD_LIGHT
          }} data-id="element-41">
              Eres un Reino.
            </span>
          </h2>
        </AnimatedSection>

        <AnimatedSection delay={0.2} data-id="element-42">
          <div className="space-y-6 text-base md:text-lg leading-relaxed text-gray-200 font-serif px-2" data-id="element-43">
            <p className="text-[17px] pt-[0px] pb-[0px] pl-[0px] pr-[0px] mt-[0px] mb-[0px] ml-[-10px] mr-[-10px]" data-id="element-44">
              Cada Reino representa un patrón emocional profundo. Una forma de
              reaccionar. De protegerte. De amar. De sabotearte. De
              transformarte.
            </p>
            <p className="text-gold-light/90 italic text-[17px] font-['DM_Sans',_sans-serif]" data-id="element-45">
              No estás entrando a un horóscopo. Estás entrando a una exploración
              de identidad.
            </p>
          </div>
        </AnimatedSection>

        {/* Cinematic divider leading into the selector */}
        <AnimatedSection delay={0.4} data-id="element-46">
          <div className="mt-20 flex flex-col items-center" data-id="element-47">
            <motion.div className="relative" animate={{
            rotate: 360
          }} transition={{
            duration: 80,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-48">
              <svg width="56" height="56" viewBox="0 0 56 56" fill="none" data-id="element-49">
                <circle cx="28" cy="28" r="26" stroke={GOLD} strokeWidth="0.6" opacity="0.6" data-id="element-50" />
                <circle cx="28" cy="28" r="18" stroke={GOLD} strokeWidth="0.5" strokeDasharray="2 3" opacity="0.7" data-id="element-51" />
                <path d="M28 10 L36 28 L28 46 L20 28 Z" stroke={GOLD} strokeWidth="0.8" fill={GOLD} fillOpacity="0.15" data-id="element-52" />
                <circle cx="28" cy="28" r="2.5" fill={GOLD} data-id="element-53" />
              </svg>
              <motion.div className="absolute inset-0 rounded-full" animate={{
              scale: [1, 1.3, 1],
              opacity: [0.3, 0.7, 0.3]
            }} transition={{
              duration: 3.5,
              repeat: Infinity,
              ease: 'easeInOut'
            }} style={{
              boxShadow: `0 0 30px ${GOLD}80`
            }} data-id="element-54" />
            </motion.div>
            <div className="mt-8 h-px w-32 bg-gradient-to-r from-transparent via-gold-primary to-transparent" data-id="element-55" />
          </div>
        </AnimatedSection>
      </div>
    </section>;
}